import {
  CloseRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  HistoryRounded,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  Grid2,
  IconButton,
  Pagination,
  Stack,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import {
  formatDate,
  formatDateWithoutTime,
} from "../../../../../views/components/common/functions/formatDate";
import { SwayyProductActivityLog } from "../../../models/products/SwayyProductActivityLog";
import { isGenericCountDetail } from "../../../models/products/swayyActivityLogDisplay";
import { SwayyProductActivityLogsStore } from "../../../stores/SwayyProductActivityLogsStore";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

interface SwayyProductActivityLogsDrawerProps {
  open: boolean;
  onClose: () => void;
  productId: number | null;
  store: SwayyProductActivityLogsStore;
}

/** Above AppBar (`theme.zIndex.drawer + 1`) so the close icon stays tappable on small screens. */
const ACTIVITY_LOGS_DRAWER_Z_OFFSET = 2000;

const displayChangeValue = (
  value: string | number | boolean | null,
): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const isClearedChangeValue = (
  value: string | number | boolean | null,
): boolean => value === null || value === undefined || value === "";

const formatChangeRange = (
  from: string | number | boolean | null,
  to: string | number | boolean | null,
): string => {
  if (isClearedChangeValue(to) && !isClearedChangeValue(from)) {
    return displayChangeValue(from);
  }
  if (isClearedChangeValue(from) && !isClearedChangeValue(to)) {
    return displayChangeValue(to);
  }
  return `${displayChangeValue(from)} → ${displayChangeValue(to)}`;
};

const groupLogsByDate = (
  logs: SwayyProductActivityLog[],
): Record<string, SwayyProductActivityLog[]> => {
  const grouped: Record<string, SwayyProductActivityLog[]> = {};
  logs.forEach((log) => {
    const dateKey = formatDateWithoutTime(log.createdAt, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(log);
  });
  return grouped;
};

const getInitials = (name: string | null): string => {
  const trimmed = name?.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return trimmed[0].toUpperCase();
};

interface LabelDiff {
  added: string[];
  removed: string[];
}

const extractListArray = (
  source: unknown,
  arrayKey: string,
): Record<string, unknown>[] => {
  if (Array.isArray(source)) return source as Record<string, unknown>[];
  if (!source || typeof source !== "object") return [];
  const candidate = (source as Record<string, unknown>)[arrayKey];
  return Array.isArray(candidate) ? (candidate as Record<string, unknown>[]) : [];
};

const getNamedListDiff = (
  log: SwayyProductActivityLog,
  arrayKey: string,
  idKey: string,
  nameKey: string,
): LabelDiff | null => {
  const before = extractListArray(log.changes?.before, arrayKey);
  const after = extractListArray(log.changes?.after, arrayKey);
  if (!before.length && !after.length) return null;

  const keyOf = (item: Record<string, unknown>): string => {
    const id = item[idKey];
    if (id !== undefined && id !== null && id !== "") return `id:${id}`;
    return JSON.stringify(item);
  };
  const labelOf = (item: Record<string, unknown>): string => {
    const name = item[nameKey];
    return name ? String(name) : "Item";
  };

  const beforeMap = new Map(before.map((item) => [keyOf(item), item]));
  const afterMap = new Map(after.map((item) => [keyOf(item), item]));
  const added: string[] = [];
  const removed: string[] = [];

  afterMap.forEach((item, key) => {
    if (!beforeMap.has(key)) added.push(labelOf(item));
  });
  beforeMap.forEach((item, key) => {
    if (!afterMap.has(key)) removed.push(labelOf(item));
  });

  if (!added.length && !removed.length) return null;
  return { added, removed };
};

const variantLabel = (variant: Record<string, unknown>): string => {
  const title = variant.name;
  const sku = variant.sku;
  if (title && sku) return `${title} (${sku})`;
  if (title) return String(title);
  if (sku) return String(sku);
  return "Variant";
};

const getVariantDiff = (log: SwayyProductActivityLog): LabelDiff | null => {
  const before = extractListArray(log.changes?.before, "variants");
  const after = extractListArray(log.changes?.after, "variants");
  if (!before.length && !after.length) return null;

  const keyOf = (variant: Record<string, unknown>): string => {
    const sku = variant.sku;
    if (typeof sku === "string" && sku.trim()) {
      return `sku:${sku.trim().toLowerCase()}`;
    }
    const id = variant.id;
    if (id !== undefined && id !== null && id !== "") return `id:${id}`;
    return JSON.stringify(variant);
  };

  const beforeMap = new Map(before.map((item) => [keyOf(item), item]));
  const afterMap = new Map(after.map((item) => [keyOf(item), item]));
  const added: string[] = [];
  const removed: string[] = [];

  afterMap.forEach((item, key) => {
    if (!beforeMap.has(key)) added.push(variantLabel(item));
  });
  beforeMap.forEach((item, key) => {
    if (!afterMap.has(key)) removed.push(variantLabel(item));
  });

  if (!added.length && !removed.length) return null;
  return { added, removed };
};

const asImageList = (source: unknown): Record<string, unknown>[] => {
  const listed = extractListArray(source, "images");
  if (listed.length) return listed;
  if (!source || typeof source !== "object") return [];
  const image = source as Record<string, unknown>;
  if (typeof image.url === "string" && image.url.trim()) return [image];
  return [];
};

const getImageDiff = (
  log: SwayyProductActivityLog,
): { added: string[]; removed: string[] } | null => {
  const before = asImageList(log.changes?.before);
  const after = asImageList(log.changes?.after);
  if (!before.length && !after.length) return null;

  const imageUrl = (image: Record<string, unknown>): string | null => {
    const url = image.url;
    if (typeof url === "string" && url.trim()) return url.trim();
    return null;
  };

  const keyOf = (image: Record<string, unknown>): string => {
    const url = imageUrl(image);
    if (url) return url;
    const id = image.id;
    if (id !== undefined && id !== null && id !== "") return `id:${id}`;
    return JSON.stringify(image);
  };

  const beforeMap = new Map(before.map((item) => [keyOf(item), item]));
  const afterMap = new Map(after.map((item) => [keyOf(item), item]));
  const added: string[] = [];
  const removed: string[] = [];

  afterMap.forEach((item, key) => {
    if (!beforeMap.has(key)) {
      added.push(imageUrl(item) || key);
    }
  });
  beforeMap.forEach((item, key) => {
    if (!afterMap.has(key)) {
      removed.push(imageUrl(item) || key);
    }
  });

  if (!added.length && !removed.length) return null;
  return { added, removed };
};

const LabelDiffSection = ({
  diff,
  entityLabel,
}: {
  diff: LabelDiff | null;
  entityLabel: string;
}) => {
  if (!diff) return null;
  return (
    <>
      {diff.added.length ? (
        <Box>
          <CustomTypography
            variant="caption"
            color={swayyColors.success}
            sx={{ fontWeight: 700 }}
          >
            {entityLabel} added ({diff.added.length})
          </CustomTypography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {diff.added.map((label) => (
              <Chip
                key={`${entityLabel}-added-${label}`}
                label={label}
                size="small"
                sx={{ height: 22, fontSize: 11, fontFamily: swayyFont }}
              />
            ))}
          </Box>
        </Box>
      ) : null}
      {diff.removed.length ? (
        <Box>
          <CustomTypography
            variant="caption"
            color={swayyColors.danger}
            sx={{ fontWeight: 700 }}
          >
            {entityLabel} removed ({diff.removed.length})
          </CustomTypography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {diff.removed.map((label) => (
              <Chip
                key={`${entityLabel}-removed-${label}`}
                label={label}
                size="small"
                sx={{ height: 22, fontSize: 11, fontFamily: swayyFont }}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </>
  );
};

const ActivityLogEntry = ({ log }: { log: SwayyProductActivityLog }) => {
  const [expanded, setExpanded] = useState(false);
  const variantDiff = getVariantDiff(log);
  const imageDiff = getImageDiff(log);
  const categoryDiff = getNamedListDiff(
    log,
    "categories",
    "category_id",
    "category_name",
  );
  const optionDiff = getNamedListDiff(
    log,
    "options",
    "option_id",
    "option_name",
  );
  const hasStructuredDiff = Boolean(
    variantDiff || imageDiff || categoryDiff || optionDiff,
  );
  const hideActiveOnVariantAddOrDelete =
    log.action === "variant.delete" ||
    log.action === "product.create_variants";
  const visibleChangeDetails = (log.changeDetails || []).filter((change) => {
    if (isGenericCountDetail(change)) return false;
    if (hideActiveOnVariantAddOrDelete && change.field === "is_active") {
      return false;
    }
    return true;
  });
  const hasChangeDetails =
    Boolean(visibleChangeDetails.length) || hasStructuredDiff;

  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: swayyRadii.full,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          color: swayyColors.white,
          backgroundColor: swayyColors.deepGreen,
          fontFamily: swayyFont,
        }}
      >
        {getInitials(log.adminName)}
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${swayyColors.border}`,
          borderRadius: swayyRadii.md,
          p: 1.5,
          bgcolor: swayyColors.white,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.75,
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Chip
              label={log.actionLabel}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                color: swayyColors.pine,
                borderColor: swayyColors.pine,
                fontFamily: swayyFont,
              }}
              variant="outlined"
            />
            {log.entityTypeLabel ? (
              <Chip
                label={log.entityTypeLabel}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  color: swayyColors.textSecondary,
                  borderColor: swayyColors.border,
                  fontFamily: swayyFont,
                }}
              />
            ) : null}
          </Box>
          <CustomTypography variant="caption" color={swayyColors.textSecondary}>
            {formatDate(log.createdAt)}
          </CustomTypography>
        </Box>

        <CustomTypography
          variant="body2"
          color={swayyColors.deepGreen}
          sx={{ lineHeight: 1.5, wordBreak: "break-word" }}
        >
          {log.summary}
        </CustomTypography>

        {hasChangeDetails ? (
          <>
            <Box
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                mt: 1,
                cursor: "pointer",
                color: swayyColors.pine,
              }}
            >
              <CustomTypography variant="caption" color={swayyColors.pine}>
                {expanded ? "Hide changes" : "View changes"}
              </CustomTypography>
              {expanded ? (
                <ExpandLessRounded sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreRounded sx={{ fontSize: 16 }} />
              )}
            </Box>
            <Collapse in={expanded}>
              <Stack
                spacing={1}
                sx={{
                  mt: 1,
                  borderRadius: swayyRadii.sm,
                  bgcolor: swayyColors.pineLight,
                  p: 1,
                }}
              >
                {visibleChangeDetails.map((change) => (
                  <Box
                    key={`${log.id}-${change.field}`}
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                  >
                    <CustomTypography
                      variant="caption"
                      color={swayyColors.deepGreen}
                      sx={{ fontWeight: 700 }}
                    >
                      {change.label}:
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color={swayyColors.textSecondary}
                    >
                      {formatChangeRange(change.from, change.to)}
                    </CustomTypography>
                  </Box>
                ))}
                <LabelDiffSection diff={variantDiff} entityLabel="Variants" />
                <LabelDiffSection
                  diff={categoryDiff}
                  entityLabel="Categories"
                />
                <LabelDiffSection diff={optionDiff} entityLabel="Options" />
                {imageDiff?.added.length ? (
                  <Box>
                    <CustomTypography
                      variant="caption"
                      color={swayyColors.success}
                      sx={{ fontWeight: 700 }}
                    >
                      Added ({imageDiff.added.length})
                    </CustomTypography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}
                    >
                      {imageDiff.added.map((url) => (
                        <Box
                          key={`image-added-${log.id}-${url}`}
                          component="a"
                          href={url.startsWith("http") ? url : undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "block",
                            width: 44,
                            height: 44,
                            borderRadius: swayyRadii.sm,
                            overflow: "hidden",
                            border: `2px solid ${swayyColors.success}`,
                          }}
                        >
                          {url.startsWith("http") ? (
                            <Box
                              component="img"
                              src={url}
                              alt="Added image"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ) : (
                            <CustomTypography variant="caption">
                              Image
                            </CustomTypography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}
                {imageDiff?.removed.length ? (
                  <Box>
                    <CustomTypography
                      variant="caption"
                      color={swayyColors.danger}
                      sx={{ fontWeight: 700 }}
                    >
                      Removed ({imageDiff.removed.length})
                    </CustomTypography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}
                    >
                      {imageDiff.removed.map((url) => (
                        <Box
                          key={`image-removed-${log.id}-${url}`}
                          component="a"
                          href={url.startsWith("http") ? url : undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "block",
                            width: 44,
                            height: 44,
                            borderRadius: swayyRadii.sm,
                            overflow: "hidden",
                            border: `2px solid ${swayyColors.danger}`,
                            opacity: 0.6,
                          }}
                        >
                          {url.startsWith("http") ? (
                            <Box
                              component="img"
                              src={url}
                              alt="Removed image"
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                                filter: "grayscale(60%)",
                              }}
                            />
                          ) : (
                            <CustomTypography variant="caption">
                              Image
                            </CustomTypography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Stack>
            </Collapse>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

const SwayyProductActivityLogsDrawer = ({
  open,
  onClose,
  productId,
  store,
}: SwayyProductActivityLogsDrawerProps) => {
  useEffect(() => {
    if (!open || !productId) return;
    void store.loadLogs(productId, 0);
  }, [open, productId, store]);

  const handlePageChange = (_event: unknown, page: number) => {
    if (!productId) return;
    void store.loadLogs(productId, (page - 1) * store.limit);
  };

  const groupedLogs = groupLogsByDate(store.logs);
  const sortedDates = Object.keys(groupedLogs).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );
  const productName = store.logs[0]?.productName;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          borderRadius: 0,
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460, md: 520 },
          maxWidth: { xs: "100vw", sm: 520 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: swayyFont,
          borderRadius: 0,
          "&.MuiPaper-root": {
            borderRadius: 0,
          },
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (theme) => theme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (theme) =>
                theme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          borderBottom: `1px solid ${swayyColors.border}`,
          backgroundColor: swayyColors.pageBg,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box display="flex" gap={1.25} alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: swayyRadii.md,
                display: "grid",
                placeItems: "center",
                backgroundColor: swayyColors.pineLight,
                color: swayyColors.pine,
              }}
            >
              <HistoryRounded fontSize="small" />
            </Box>
            <Box minWidth={0}>
              <CustomTypography
                variant="heading6"
                color={swayyColors.deepGreen}
                sx={{ fontWeight: 700 }}
              >
                Activity Logs
              </CustomTypography>
              {productName ? (
                <CustomTypography
                  variant="caption"
                  color={swayyColors.textSecondary}
                  sx={{ display: "block" }}
                >
                  {productName}
                </CustomTypography>
              ) : null}
            </Box>
          </Box>
          <IconButton
            aria-label="Close activity logs"
            onClick={onClose}
            size="small"
            sx={{
              flexShrink: 0,
              color: swayyColors.deepGreen,
              border: `1px solid ${swayyColors.border}`,
              borderRadius: swayyRadii.sm,
              backgroundColor: swayyColors.white,
              "&:hover": {
                backgroundColor: swayyColors.pineLight,
              },
            }}
          >
            <CloseRounded />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 2 }}>
        {store.isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 240,
            }}
          >
            <CircularProgress size={32} sx={{ color: swayyColors.pine }} />
          </Box>
        ) : store.errorMessage ? (
          <CustomTypography variant="body2" color={swayyColors.danger}>
            {store.errorMessage}
          </CustomTypography>
        ) : store.logs.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 8 }}>
            <HistoryRounded sx={{ fontSize: 42, color: swayyColors.border }} />
            <CustomTypography
              variant="body2"
              color={swayyColors.textSecondary}
            >
              No activity recorded for this product yet.
            </CustomTypography>
          </Stack>
        ) : (
          sortedDates.map((date) => (
            <Box key={date} sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    height: "1px",
                    flex: 1,
                    backgroundColor: swayyColors.border,
                  }}
                />
                <CustomTypography
                  variant="caption"
                  color={swayyColors.textSecondary}
                  sx={{
                    px: 1.25,
                    py: 0.4,
                    fontWeight: 700,
                    backgroundColor: swayyColors.pineLight,
                    borderRadius: swayyRadii.full,
                  }}
                >
                  {date}
                </CustomTypography>
                <Box
                  sx={{
                    height: "1px",
                    flex: 1,
                    backgroundColor: swayyColors.border,
                  }}
                />
              </Box>
              <Stack spacing={1.5}>
                {groupedLogs[date].map((log) => (
                  <ActivityLogEntry key={log.id} log={log} />
                ))}
              </Stack>
            </Box>
          ))
        )}
      </Box>

      {store.total > 0 ? (
        <Grid2
          container
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${swayyColors.border}`,
          }}
        >
          <Grid2 size={{ xs: 12, sm: "auto" }}>
            <CustomTypography
              variant="caption"
              color={swayyColors.textSecondary}
            >
              {store.total} {store.total === 1 ? "entry" : "entries"}
            </CustomTypography>
          </Grid2>
          <Grid2 size={{ xs: 12, sm: "auto" }}>
            <Pagination
              count={Math.max(store.totalPages, 1)}
              page={store.page}
              disabled={store.isLoading}
              onChange={handlePageChange}
              size="small"
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: swayyColors.pine,
                  color: swayyColors.white,
                },
              }}
            />
          </Grid2>
        </Grid2>
      ) : null}
    </Drawer>
  );
};

export default observer(SwayyProductActivityLogsDrawer);
